import { useScreenSize } from '@/hooks/useScreenSize'
import {
  getPreferredFirstPanelWidth,
  getPreferredFirstPanelWidthFromCookieString,
  updatePreferredFirstPanelWidth,
} from '@/lib/features/layoutSlice'
import { useCallback, useEffect, useRef, useState } from 'react'

// Constants
const WIDTH_CONSTRAINTS = {
  DEFAULT: 50,
  MAX: 50,
  MIN_PIXELS: 352, // 320px + 32px padding
} as const

const ONE_CARD_THRESHOLD = 50

// Server-side compatible initialization
const getInitialWidthFromCookies = (cookieString?: string): number => {
  if (cookieString) {
    const storedWidth = getPreferredFirstPanelWidthFromCookieString(cookieString)
    if (storedWidth !== undefined) {
      if (storedWidth > WIDTH_CONSTRAINTS.MAX) {
        return WIDTH_CONSTRAINTS.MAX
      }

      return storedWidth
    }
  }
  if (typeof window !== 'undefined') {
    const storedWidth = getPreferredFirstPanelWidth()
    if (storedWidth !== undefined) {
      return storedWidth
    }
  }
  return WIDTH_CONSTRAINTS.DEFAULT
}

// Custom hook for resizable panel logic
export function useResizablePanel(initialCookieString?: string) {
  const [firstPanelWidth, setFirstPanelWidth] = useState<number>(() =>
    getInitialWidthFromCookies(initialCookieString)
  )
  const [isDragging, setIsDragging] = useState(false)
  const [preferredFirstPanelWidth, setPreferredFirstPanelWidth] = useState<number>(() =>
    getInitialWidthFromCookies(initialCookieString)
  )
  const containerRef = useRef<HTMLDivElement>(null)
  const screenSize = useScreenSize()

  // override oneCardMode for mobile view
  const isMobile = screenSize === 'xs' || screenSize === 'sm'
  const oneCardMode = isMobile || firstPanelWidth > ONE_CARD_THRESHOLD

  const calculateMinWidthPercent = useCallback((containerWidth: number): number => {
    return (WIDTH_CONSTRAINTS.MIN_PIXELS / containerWidth) * 100
  }, [])

  const constrainWidth = useCallback((width: number, minWidthPercent: number): number => {
    return Math.max(minWidthPercent, Math.min(WIDTH_CONSTRAINTS.MAX, width))
  }, [])

  const handleSplitterMouseDown = useCallback((_e: React.MouseEvent) => {
    setIsDragging(true)
  }, [])

  const handleSplitterDoubleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setFirstPanelWidth(WIDTH_CONSTRAINTS.DEFAULT)
    setPreferredFirstPanelWidth(WIDTH_CONSTRAINTS.DEFAULT)
    updatePreferredFirstPanelWidth(WIDTH_CONSTRAINTS.DEFAULT)
  }, [])

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return

      const containerRect = containerRef.current.getBoundingClientRect()
      const splitterWidthPx = 16 // w-4 in Tailwind (1rem = 16px)
      const mouseRelativeX = e.clientX - containerRect.left
      const centerX = mouseRelativeX - splitterWidthPx / 2
      const newWidth = (centerX / containerRect.width) * 100
      const minWidthPercent = calculateMinWidthPercent(containerRect.width)
      const constrainedWidth = constrainWidth(newWidth, minWidthPercent)

      setFirstPanelWidth(constrainedWidth)

      if (constrainedWidth <= WIDTH_CONSTRAINTS.MAX) {
        setPreferredFirstPanelWidth(constrainedWidth)
        updatePreferredFirstPanelWidth(constrainedWidth)
      }
    },
    [isDragging, calculateMinWidthPercent, constrainWidth]
  )

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  const handleWindowResize = useCallback(() => {
    if (!containerRef.current) return

    const containerRect = containerRef.current.getBoundingClientRect()
    const minWidthPercent = calculateMinWidthPercent(containerRect.width)

    if (firstPanelWidth < minWidthPercent) {
      setFirstPanelWidth(minWidthPercent)
    }

    if (oneCardMode && minWidthPercent < WIDTH_CONSTRAINTS.MAX) {
      const targetWidth = Math.max(minWidthPercent, preferredFirstPanelWidth)
      setFirstPanelWidth(targetWidth)
    }
  }, [firstPanelWidth, oneCardMode, preferredFirstPanelWidth, calculateMinWidthPercent])

  // Event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    } else {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize)
    return () => window.removeEventListener('resize', handleWindowResize)
  }, [handleWindowResize])

  return {
    firstPanelWidth,
    setFirstPanelWidth,
    isDragging,
    oneCardMode,
    containerRef,
    handleSplitterMouseDown,
    handleSplitterDoubleClick,
  }
}
